import { useState, useEffect, useCallback, useRef } from 'react';
import type { ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../stores/useAuthStore';
import { getAuthToken } from '../../services/api';
import { AppleEmoji } from '../../components/AppleEmoji';
import { cardBank, cardScheme } from '../../utils/card';

const API_BASE = '/api/admin';

type Tab = 'dashboard' | 'orders' | 'users' | 'prices' | 'payments' | 'promos' | 'settings' | 'faq' | 'mock';

const NAV: { tab: Tab; emoji: string; label: string }[] = [
  { tab: 'dashboard', emoji: '📊', label: 'Дашборд' },
  { tab: 'orders', emoji: '📦', label: 'Заказы' },
  { tab: 'users', emoji: '👥', label: 'Пользователи' },
  { tab: 'prices', emoji: '💰', label: 'Цены' },
  { tab: 'payments', emoji: '💳', label: 'Способы оплаты' },
  { tab: 'promos', emoji: '🎟️', label: 'Промокоды' },
  { tab: 'settings', emoji: '⚙️', label: 'Настройки' },
  { tab: 'faq', emoji: '❓', label: 'FAQ' },
  { tab: 'mock', emoji: '🧪', label: 'Тест. чеки' },
];

/* ---------- Таблицы админки: подписи, форматирование, мелкие компоненты ---------- */

const ORDER_STATUS_LABELS: Record<string, string> = {
  pending: 'Создан',
  waiting_payment: 'Ждёт оплату',
  paid: 'Оплачен',
  processing: 'В работе',
  completed: 'Выполнен',
  failed: 'Ошибка',
  cancelled: 'Отменён',
  expired: 'Истёк',
};

// Статусы выплаты Velox (продажа USDT).
const PAYOUT_LABELS: Record<string, string> = {
  Pending: 'создаётся',
  Sent: 'в пути',
  Completed: 'на карте',
  Reversed: 'вернулась, можно повторить',
  Unresolved: 'уточняется, не повторять',
};

const SUMSUB_LABELS: Record<string, string> = {
  init: 'Начал проверку',
  pending: 'На проверке',
  approved: 'Пройден',
  rejected: 'Отклонён',
};

function fmtRub(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return `${Number(value).toLocaleString('ru-RU', { maximumFractionDigits: 2 })} ₽`;
}

function fmtUsdt(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return `${Number(value).toLocaleString('ru-RU', { maximumFractionDigits: 4 })} USDT`;
}

function fmtDate(value: string): string {
  return new Date(value).toLocaleDateString('ru-RU');
}

function fmtDateTime(value: string): string {
  return new Date(value).toLocaleString('ru-RU', {
    day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit',
  });
}

/** +79156589163 → +7 915 658-91-63; остальные страны — как есть. */
function fmtPhone(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('7')) {
    return `+7 ${digits.slice(1, 4)} ${digits.slice(4, 7)}-${digits.slice(7, 9)}-${digits.slice(9)}`;
  }
  return phone;
}

function shortAddress(address: string): string {
  return address.length > 14 ? `${address.slice(0, 6)}…${address.slice(-5)}` : address;
}

/** Банк и платёжная система по маске «2200 70** **** 6781»: первые 6 цифр в ней есть. */
function CardBankLabel({ mask }: { mask: string }) {
  const bin = mask.replace(/\s/g, '').slice(0, 6);
  const [bank, setBank] = useState<string | null>(null);
  useEffect(() => {
    if (!/^\d{6}$/.test(bin)) return;
    let alive = true;
    cardBank(bin).then((name) => alive && setBank(name));
    return () => {
      alive = false;
    };
  }, [bin]);
  const scheme = cardScheme(bin);
  if (!bank && !scheme) return null;
  return <span className="ax-sub">{[bank, scheme].filter(Boolean).join(' · ')}</span>;
}

function StatusChip({ status, label }: { status: string; label?: string }) {
  return <span className={`ax-chip ax-status ax-status--${status}`}>{label || status}</span>;
}

function CopyText({ value, title, mono, children }: {
  value: string; title: string; mono?: boolean; children: ReactNode;
}) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      /* буфер недоступен — ничего страшного, значение видно в подсказке */
    }
  };
  return (
    <button type="button" className={'ax-copy' + (mono ? ' ax-mono' : '')} onClick={copy} title={`${title}: ${value}`}>
      {copied ? 'Скопировано' : children}
    </button>
  );
}

interface RowMenuItem {
  label: string;
  onClick: () => void;
  danger?: boolean;
  disabled?: boolean;
}

/** Меню «⋯» строки таблицы. Позиция fixed: таблица прокручивается по
 *  горизонтали, и обычный absolute-список обрезался бы её краем. */
function RowMenu({ items }: { items: RowMenuItem[] }) {
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!pos) return;
    const close = (e: Event) => {
      const target = e.target as Node;
      if (listRef.current?.contains(target) || buttonRef.current?.contains(target)) return;
      setPos(null);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setPos(null);
    const onMove = () => setPos(null);
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [pos]);

  const toggle = () => {
    if (pos) {
      setPos(null);
      return;
    }
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;
    // У нижнего края экрана раскрываем вверх.
    const below = window.innerHeight - rect.bottom > 48 * items.length + 16;
    setPos({
      top: below ? rect.bottom + 4 : rect.top - 4 - (40 * items.length + 8),
      right: window.innerWidth - rect.right,
    });
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={'ax-icon-btn' + (pos ? ' ax-icon-btn--active' : '')}
        onClick={toggle}
        aria-haspopup="menu"
        aria-expanded={Boolean(pos)}
        aria-label="Ещё действия"
        title="Ещё действия"
      >
        <IconDots />
      </button>
      {pos && (
        <div ref={listRef} className="ax-menu" role="menu" style={{ top: pos.top, right: pos.right }}>
          {items.map((item) => (
            <button
              key={item.label}
              type="button"
              role="menuitem"
              className={'ax-menu__item' + (item.danger ? ' ax-menu__item--danger' : '')}
              disabled={item.disabled}
              onClick={() => {
                setPos(null);
                item.onClick();
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </>
  );
}

const svgProps = {
  width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor',
  strokeWidth: 2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, 'aria-hidden': true,
};

function IconRefresh() {
  return (
    <svg {...svgProps}><path d="M21 12a9 9 0 1 1-2.64-6.36" /><path d="M21 3v6h-6" /></svg>
  );
}

function IconDots() {
  return (
    <svg {...svgProps}><circle cx="5" cy="12" r="1.4" /><circle cx="12" cy="12" r="1.4" /><circle cx="19" cy="12" r="1.4" /></svg>
  );
}

interface Stats {
  status_counts: Record<string, number>;
  pending_count: number;
  today: { orders: number; revenue: number };
  week: { orders: number; revenue: number };
  total: { orders: number; revenue: number; users: number };
}

interface Order {
  id: number;
  uuid: string;
  status: string;
  /** sell — клиент продаёт USDT, выплата на карту через Velox по кнопке */
  direction?: 'buy' | 'sell';
  card_mask?: string | null;
  sell_rate?: number | null;
  payout_id?: number | null;
  payout_status?: string | null;
  payout_error?: string | null;
  tx_hash?: string | null;
  usdt_amount: number | null;
  wallet_address: string | null;
  wallet_network: string | null;
  amount_rub: number;
  payment_provider: string | null;
  admin_notes: string | null;
  created_at: string;
  paid_at: string | null;
  completed_at: string | null;
}

interface User {
  id: number;
  email: string | null;
  phone_number: string | null;
  email_verified: boolean;
  phone_verified: boolean;
  is_fully_verified: boolean;
  is_admin: boolean;
  kyc_level: number;
  sumsub_status: string | null;
  telegram_id: number | null;
  username: string | null;
  first_name: string | null;
  last_name: string | null;
  is_premium: boolean;
  created_at: string;
}

interface PriceConfig {
  id: number;
  product_type: string;
  base_price: number;
  network: string | null;
  icon_url: string | null;
  decimals: number;
  transfer_fee: number;
  rate_symbol: string | null;
  min_amount: number;
  max_amount: number;
  discount_threshold: number | null;
  discount_percent: number;
  label: string | null;
  description: string | null;
  is_active: boolean;
  sort_order: number;
}

interface PaymentMethod {
  id: number;
  code: string;
  name: string;
  description: string | null;
  icon_url: string | null;
  commission_percent: number;
  commission_fixed: number;
  commission_text: string | null;
  min_amount: number;
  max_amount: number | null;
  provider_code: string | null;
  is_active: boolean;
  sort_order: number;
}

interface PromoCode {
  id: number;
  code: string;
  discount_type: string;
  discount_value: number;
  max_uses: number | null;
  used_count: number;
  min_order_amount: number | null;
  max_discount_amount: number | null;
  valid_from: string | null;
  valid_until: string | null;
  product_types: string | null;
  is_active: boolean;
  created_at: string;
}

interface Setting {
  id: number;
  key: string;
  value: string | null;
  type: string;
  category: string;
  label: string | null;
  description: string | null;
  is_public: boolean;
}

interface FAQItem {
  id: number;
  question: string;
  answer: string;
  sort_order: number;
  is_active: boolean;
}

interface MockOrderForm {
  usdt_amount: number | null;
  wallet_address: string | null;
  wallet_network: string | null;
  amount_rub: number;
  status: string;
  payment_provider: string;
}

function getToken(): string | null {
  return getAuthToken();
}

export function AdminPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token, logout, refreshUser } = useAuthStore();

  const [activeTab, setActiveTab] = useState<Tab>('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [stats, setStats] = useState<Stats | null>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const [ordersTotal, setOrdersTotal] = useState(0);
  const [users, setUsers] = useState<User[]>([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [prices, setPrices] = useState<PriceConfig[]>([]);
  const [payments, setPayments] = useState<PaymentMethod[]>([]);
  const [promos, setPromos] = useState<PromoCode[]>([]);
  const [settings, setSettings] = useState<Setting[]>([]);

  const [orderStatusFilter, setOrderStatusFilter] = useState('');
  const [orderSearch, setOrderSearch] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [settingsCategory, setSettingsCategory] = useState('');

  const [faqItems, setFaqItems] = useState<FAQItem[]>([]);
  const [editingFaq, setEditingFaq] = useState<FAQItem | null>(null);

  const [editingPrice, setEditingPrice] = useState<PriceConfig | null>(null);
  const [editingPayment, setEditingPayment] = useState<PaymentMethod | null>(null);
  const [editingPromo, setEditingPromo] = useState<PromoCode | null>(null);
  const [editingSetting, setEditingSetting] = useState<Setting | null>(null);
  const [editingOrder, setEditingOrder] = useState<Order | null>(null);
  const [showMockModal, setShowMockModal] = useState(false);
  const [mockOrders, setMockOrders] = useState<Order[]>([]);
  const [viewingReceipt, setViewingReceipt] = useState<Order | null>(null);

  // Guard: need a logged-in admin. Non-admin → /account, guests → /login.
  useEffect(() => {
    if (!token) {
      navigate('/login', { state: { from: location.pathname } });
      return;
    }
    refreshUser();
  }, [token]);

  useEffect(() => {
    if (user && !user.is_admin) {
      navigate('/account', { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    setSidebarOpen(false);
  }, [activeTab]);

  const isAuthenticated = Boolean(user?.is_admin);

  const fetchApi = useCallback(async (endpoint: string, options?: RequestInit) => {
    const authToken = getToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...((options?.headers as Record<string, string>) || {}),
    };
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }

    const res = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers,
    });

    if (res.status === 401) {
      logout();
      navigate('/login');
      throw new Error('Session expired');
    }

    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.detail || `Error: ${res.status}`);
    }

    return res.json();
  }, [logout, navigate]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const toggleUserAdmin = async (u: User) => {
    if (!confirm(u.is_admin ? `Снять права админа с ${u.email}?` : `Выдать права админа ${u.email}?`)) return;
    try {
      await fetchApi(`/users/${u.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ is_admin: !u.is_admin }),
      });
      loadUsers();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const deleteUser = async (u: User) => {
    if (!confirm(`Удалить пользователя ${u.email}? Операция необратима.`)) return;
    try {
      await fetchApi(`/users/${u.id}`, { method: 'DELETE' });
      loadUsers();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  // Data loaders
  const loadStats = useCallback(async () => {
    const data = await fetchApi('/stats');
    setStats(data);
  }, [fetchApi]);

  const loadOrders = useCallback(async () => {
    const params = new URLSearchParams();
    if (orderStatusFilter) params.append('status', orderStatusFilter);
    if (orderSearch) params.append('search', orderSearch);
    const data = await fetchApi(`/orders?${params}`);
    setOrders(data.orders);
    setOrdersTotal(data.total);
  }, [fetchApi, orderStatusFilter, orderSearch]);

  const loadUsers = useCallback(async () => {
    const params = new URLSearchParams();
    if (userSearch) params.append('search', userSearch);
    const data = await fetchApi(`/users?${params}`);
    setUsers(data.users);
    setUsersTotal(data.total);
  }, [fetchApi, userSearch]);

  const loadPrices = useCallback(async () => {
    const data = await fetchApi('/prices');
    setPrices(data.prices);
  }, [fetchApi]);

  const loadPayments = useCallback(async () => {
    const data = await fetchApi('/payment-methods');
    setPayments(data.methods);
  }, [fetchApi]);

  const loadPromos = useCallback(async () => {
    const data = await fetchApi('/promo-codes');
    setPromos(data.codes);
  }, [fetchApi]);

  const loadSettings = useCallback(async () => {
    const params = new URLSearchParams();
    if (settingsCategory) params.append('category', settingsCategory);
    const data = await fetchApi(`/settings?${params}`);
    setSettings(data.settings);
  }, [fetchApi, settingsCategory]);

  const loadFaqs = useCallback(async () => {
    const data = await fetchApi('/faqs');
    setFaqItems(data.faqs);
  }, [fetchApi]);

  // Create mock order
  const createMockOrder = async (form: MockOrderForm) => {
    try {
      const result = await fetchApi('/mock-orders', {
        method: 'POST',
        body: JSON.stringify(form),
      });
      if (result.success) {
        setMockOrders(prev => [result.order, ...prev]);
        setShowMockModal(false);
        setViewingReceipt(result.order); // Show receipt after creation
        loadStats();
      }
    } catch (err) {
      setError((err as Error).message);
    }
  };

  // Load data on tab change
  useEffect(() => {
    if (!isAuthenticated) return;

    setIsLoading(true);
    setError(null);

    const loadData = async () => {
      try {
        switch (activeTab) {
          case 'dashboard': await loadStats(); break;
          case 'orders': await loadOrders(); break;
          case 'users': await loadUsers(); break;
          case 'prices': await loadPrices(); break;
          case 'payments': await loadPayments(); break;
          case 'promos': await loadPromos(); break;
          case 'settings': await loadSettings(); break;
          case 'faq': await loadFaqs(); break;
        }
      } catch (err) {
        setError((err as Error).message);
      }
      setIsLoading(false);
    };

    loadData();
  }, [isAuthenticated, activeTab, loadStats, loadOrders, loadUsers, loadPrices, loadPayments, loadPromos, loadSettings, loadFaqs]);

  // Order actions
  const updateOrderStatus = async (uuid: string, newStatus: string) => {
    try {
      await fetchApi(`/orders/${uuid}/status?new_status=${newStatus}`, { method: 'POST' });
      loadOrders();
      loadStats();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  // Продажа: ручное одобрение выплаты на карту через Velox
  const approvePayout = async (order: Order) => {
    const ok = confirm(
      `Выплатить ${order.amount_rub.toLocaleString('ru-RU')} ₽ на карту ${order.card_mask || ''}?\n\n` +
      `Убедитесь, что ${order.usdt_amount} USDT клиента пришли на счёт Velox.`
    );
    if (!ok) return;
    try {
      await fetchApi(`/orders/${order.uuid}/payout`, { method: 'POST' });
      loadOrders();
      loadStats();
    } catch (err) {
      setError((err as Error).message);
      loadOrders();
    }
  };

  const refreshPayout = async (uuid: string) => {
    try {
      await fetchApi(`/orders/${uuid}/payout/refresh`, { method: 'POST' });
      loadOrders();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const deleteOrder = async (uuid: string) => {
    if (!confirm('Удалить заказ?')) return;
    try {
      await fetchApi(`/orders/${uuid}`, { method: 'DELETE' });
      loadOrders();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  // Price actions
  const savePrice = async (price: Partial<PriceConfig>) => {
    try {
      if (price.id) {
        await fetchApi(`/prices/${price.id}`, { method: 'PUT', body: JSON.stringify(price) });
      } else {
        await fetchApi('/prices', { method: 'POST', body: JSON.stringify(price) });
      }
      setEditingPrice(null);
      loadPrices();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const deletePrice = async (id: number) => {
    if (!confirm('Удалить цену?')) return;
    try {
      await fetchApi(`/prices/${id}`, { method: 'DELETE' });
      loadPrices();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  // Payment actions
  const savePayment = async (payment: Partial<PaymentMethod>) => {
    try {
      if (payment.id) {
        await fetchApi(`/payment-methods/${payment.id}`, { method: 'PUT', body: JSON.stringify(payment) });
      } else {
        await fetchApi('/payment-methods', { method: 'POST', body: JSON.stringify(payment) });
      }
      setEditingPayment(null);
      loadPayments();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const deletePayment = async (id: number) => {
    if (!confirm('Удалить способ оплаты?')) return;
    try {
      await fetchApi(`/payment-methods/${id}`, { method: 'DELETE' });
      loadPayments();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  // Promo actions
  const savePromo = async (promo: Partial<PromoCode>) => {
    try {
      if (promo.id) {
        await fetchApi(`/promo-codes/${promo.id}`, { method: 'PUT', body: JSON.stringify(promo) });
      } else {
        await fetchApi('/promo-codes', { method: 'POST', body: JSON.stringify(promo) });
      }
      setEditingPromo(null);
      loadPromos();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const deletePromo = async (id: number) => {
    if (!confirm('Удалить промокод?')) return;
    try {
      await fetchApi(`/promo-codes/${id}`, { method: 'DELETE' });
      loadPromos();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  // Setting actions
  const saveSetting = async (setting: Partial<Setting>) => {
    try {
      if (setting.id) {
        await fetchApi(`/settings/${setting.key}`, { method: 'PUT', body: JSON.stringify(setting) });
      } else {
        await fetchApi('/settings', { method: 'POST', body: JSON.stringify(setting) });
      }
      setEditingSetting(null);
      loadSettings();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const deleteSetting = async (key: string) => {
    if (!confirm('Удалить настройку?')) return;
    try {
      await fetchApi(`/settings/${key}`, { method: 'DELETE' });
      loadSettings();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const saveFaq = async (faq: Partial<FAQItem>) => {
    try {
      if (faq.id) {
        await fetchApi(`/faqs/${faq.id}`, { method: 'PUT', body: JSON.stringify(faq) });
      } else {
        await fetchApi('/faqs', { method: 'POST', body: JSON.stringify(faq) });
      }
      setEditingFaq(null);
      loadFaqs();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const deleteFaq = async (id: number) => {
    if (!confirm('Удалить вопрос?')) return;
    try {
      await fetchApi(`/faqs/${id}`, { method: 'DELETE' });
      loadFaqs();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  // Init defaults
  const initDefaults = async () => {
    try {
      const result = await fetchApi('/init-defaults', { method: 'POST' });
      alert(`Создано: ${result.created.settings} настроек, ${result.created.prices} цен, ${result.created.methods} способов оплаты`);
      loadPrices();
      loadPayments();
      loadSettings();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  // Status labels & colors
  const statusLabels = ORDER_STATUS_LABELS;

  const categoryLabels: Record<string, string> = {
    general: 'Общие', texts: 'Тексты', links: 'Ссылки', footer: 'Футер', images: 'Изображения', payments: 'Платежи',
  };

  if (!user) {
    return (
      <div className="account-layout__loading">
        <div className="account-layout__spinner" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  const activeNav = NAV.find((n) => n.tab === activeTab);

  return (
    <div className="account-layout admin-shell">
      {sidebarOpen && <div className="account-layout__overlay" onClick={() => setSidebarOpen(false)} />}

      <aside className={`account-sidebar ${sidebarOpen ? 'account-sidebar--open' : ''}`}>
        <div className="account-sidebar__logo">
          <span>RubExchange</span>
          <span className="account-sidebar__logo-sub"> admin</span>
        </div>

        <button className="account-sidebar__back" onClick={() => navigate('/account')}>
          <AppleEmoji emoji="👤" size={18} />
          <span>В личный кабинет</span>
        </button>

        <div className="account-sidebar__user">
          <div className="account-sidebar__avatar">
            <AppleEmoji emoji="🛡" size={28} />
          </div>
          <div className="account-sidebar__user-info">
            <div className="account-sidebar__user-email">{user.email || '—'}</div>
            <div className="account-sidebar__user-status">
              <AppleEmoji emoji="⚙️" size={12} /> Администратор
            </div>
          </div>
        </div>

        <nav className="account-sidebar__nav">
          {NAV.map((item) => (
            <button
              key={item.tab}
              className={'account-sidebar__link' + (activeTab === item.tab ? ' account-sidebar__link--active' : '')}
              onClick={() => setActiveTab(item.tab)}
            >
              <span className="account-sidebar__icon">
                <AppleEmoji emoji={item.emoji} size={22} />
              </span>
              <span className="account-sidebar__label">{item.label}</span>
            </button>
          ))}
        </nav>

        <button className="account-sidebar__logout" onClick={handleLogout}>
          <AppleEmoji emoji="🚪" size={18} />
          <span>Выйти</span>
        </button>
      </aside>

      <main className="account-main">
        <header className="account-main__mobile-header">
          <button
            className="account-main__burger"
            onClick={() => setSidebarOpen(true)}
            aria-label="menu"
          >
            <span /><span /><span />
          </button>
          <div className="account-main__mobile-title">{activeNav?.label || 'Админ-панель'}</div>
        </header>

        <div className="account-main__content admin-page admin-page--embedded">
          <div className="admin-topbar">
            <h1 className="admin-topbar__title">{activeNav?.label || 'Админ'}</h1>
            <div className="admin-topbar__actions">
              <button className="admin-btn admin-btn--secondary" onClick={initDefaults}>
                Инициализировать
              </button>
            </div>
          </div>

          {error && (
            <div className="admin-error">
              {error}
              <button onClick={() => setError(null)}>×</button>
            </div>
          )}

          <div className="admin-page__main">
        {isLoading && <div className="admin-loading">Загрузка...</div>}

        {/* Dashboard */}
        {activeTab === 'dashboard' && stats && !isLoading && (
          <div className="admin-dashboard">
            <div className="admin-stats">
              <div className="admin-stat-card">
                <div className="admin-stat-card__label">Ожидают выдачи</div>
                <div className="admin-stat-card__value">{stats.pending_count}</div>
              </div>
              <div className="admin-stat-card">
                <div className="admin-stat-card__label">Заказов сегодня</div>
                <div className="admin-stat-card__value">{stats.today.orders}</div>
                <div className="admin-stat-card__sub">{stats.today.revenue.toLocaleString()} ₽</div>
              </div>
              <div className="admin-stat-card">
                <div className="admin-stat-card__label">За неделю</div>
                <div className="admin-stat-card__value">{stats.week.orders}</div>
                <div className="admin-stat-card__sub">{stats.week.revenue.toLocaleString()} ₽</div>
              </div>
              <div className="admin-stat-card">
                <div className="admin-stat-card__label">Всего выручка</div>
                <div className="admin-stat-card__value">{stats.total.revenue.toLocaleString()} ₽</div>
                <div className="admin-stat-card__sub">{stats.total.orders} заказов</div>
              </div>
            </div>

            <div className="admin-status-pills">
              {Object.entries(stats.status_counts).map(([status, count]) => (
                <div key={status} className={`admin-status-pill ${status}`}>
                  {statusLabels[status] || status}: {count}
                </div>
              ))}
            </div>

            <div className="admin-stat-card">
              <div className="admin-stat-card__label">Пользователей</div>
              <div className="admin-stat-card__value">{stats.total.users}</div>
            </div>
          </div>
        )}

        {/* Orders */}
        {activeTab === 'orders' && !isLoading && (
          <div>
            <div className="ax-filters" role="tablist" aria-label="Статус заказа">
              {['', 'paid', 'processing', 'waiting_payment', 'completed', 'cancelled', 'expired'].map((status) => (
                <button
                  key={status || 'all'}
                  type="button"
                  role="tab"
                  aria-selected={orderStatusFilter === status}
                  className={'ax-filter' + (orderStatusFilter === status ? ' ax-filter--active' : '')}
                  onClick={() => setOrderStatusFilter(status)}
                >
                  {status ? statusLabels[status] : 'Все'}
                </button>
              ))}
            </div>

            <div className="admin-search">
              <input
                type="text"
                placeholder="Поиск по номеру заказа или адресу кошелька"
                value={orderSearch}
                onChange={(e) => setOrderSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && loadOrders()}
              />
              <button onClick={loadOrders}>Поиск</button>
            </div>

            <div className="ax-card">
              <div className="ax-card__head">
                <h2 className="ax-card__title">
                  Заказы <span className="ax-card__count">{ordersTotal}</span>
                </h2>
                <button className="ax-btn ax-btn--ghost" onClick={loadOrders}>
                  <IconRefresh /> Обновить
                </button>
              </div>

              <div className="ax-table-wrap">
                <table className="ax-table">
                  <thead>
                    <tr>
                      <th>Заказ</th>
                      <th className="ax-num">Сумма</th>
                      <th>Куда отправить</th>
                      <th>Статус</th>
                      <th className="ax-right">Действия</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order) => {
                      const isSell = order.direction === 'sell';
                      const open = ['paid', 'processing'].includes(order.status);
                      const canPayout = isSell && !['completed', 'cancelled'].includes(order.status)
                        && (!order.payout_id || order.payout_status === 'Reversed');
                      return (
                        <tr key={order.uuid}>
                          <td>
                            <CopyText value={order.uuid} title="Скопировать полный номер заказа">
                              #{order.uuid.slice(0, 8)}
                            </CopyText>
                            <div className="ax-meta">
                              <span className={`ax-dir ax-dir--${isSell ? 'sell' : 'buy'}`}>
                                {isSell ? 'Продажа' : 'Покупка'}
                              </span>
                              <span className="ax-sub ax-sub--date">{fmtDateTime(order.created_at)}</span>
                            </div>
                          </td>
                          <td className="ax-num">
                            <div className="ax-strong">{fmtRub(order.amount_rub)}</div>
                            <div className="ax-sub">{fmtUsdt(order.usdt_amount)}</div>
                          </td>
                          <td>
                            {isSell ? (
                              <div className="ax-stack">
                                <span className="ax-mono">{order.card_mask || 'карта не указана'}</span>
                                {order.card_mask && <CardBankLabel mask={order.card_mask} />}
                                {order.tx_hash && (
                                  <a className="ax-link" href={`https://tronscan.org/#/transaction/${order.tx_hash}`}
                                     target="_blank" rel="noopener noreferrer">
                                    Перевод клиента ↗
                                  </a>
                                )}
                              </div>
                            ) : order.wallet_address ? (
                              <div className="ax-row">
                                <CopyText value={order.wallet_address} title="Скопировать адрес целиком" mono>
                                  {shortAddress(order.wallet_address)}
                                </CopyText>
                                {order.wallet_network && <span className="ax-net">{order.wallet_network}</span>}
                              </div>
                            ) : (
                              <span className="ax-muted">—</span>
                            )}
                          </td>
                          <td>
                            <StatusChip status={order.status} label={statusLabels[order.status]} />
                            {isSell && order.payout_status && (
                              <div className="ax-sub ax-sub--status">
                                Выплата {PAYOUT_LABELS[order.payout_status] || order.payout_status}
                                {order.payout_id && <span className="ax-muted"> · #{order.payout_id}</span>}
                              </div>
                            )}
                            {isSell && order.payout_error && (
                              <div className="ax-sub ax-sub--status ax-sub--error">{order.payout_error}</div>
                            )}
                          </td>
                          <td className="ax-right">
                            <div className="ax-actions">
                              {canPayout ? (
                                <button className="ax-btn ax-btn--primary" onClick={() => approvePayout(order)}>
                                  Выплатить на карту
                                </button>
                              ) : isSell && order.payout_id ? (
                                <button className="ax-btn ax-btn--ghost" onClick={() => refreshPayout(order.uuid)}>
                                  Проверить выплату
                                </button>
                              ) : !isSell && open ? (
                                <button className="ax-btn ax-btn--primary" onClick={() => updateOrderStatus(order.uuid, 'completed')}
                                        title="USDT отправлены клиенту — закрыть заказ">
                                  USDT отправлены
                                </button>
                              ) : null}
                              <RowMenu
                                items={[
                                  { label: 'Подробнее', onClick: () => setEditingOrder(order) },
                                  ...(!isSell && order.status === 'paid'
                                    ? [{ label: 'Взять в работу', onClick: () => updateOrderStatus(order.uuid, 'processing') }]
                                    : []),
                                  ...(canPayout && order.payout_id
                                    ? [{ label: 'Проверить выплату', onClick: () => refreshPayout(order.uuid) }]
                                    : []),
                                  ...(open
                                    ? [{ label: 'Отменить заказ', danger: true, onClick: () => updateOrderStatus(order.uuid, 'cancelled') }]
                                    : []),
                                  { label: 'Удалить заказ', danger: true, onClick: () => deleteOrder(order.uuid) },
                                ]}
                              />
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {orders.length === 0 && <div className="ax-empty">Заказов нет</div>}
            </div>
          </div>
        )}

        {/* Users */}
        {activeTab === 'users' && !isLoading && (
          <div>
            <div className="admin-search">
              <input
                type="text"
                placeholder="Поиск по email / телефону / имени..."
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && loadUsers()}
              />
              <button onClick={loadUsers}>Поиск</button>
            </div>

            <div className="ax-card">
              <div className="ax-card__head">
                <h2 className="ax-card__title">
                  Пользователи <span className="ax-card__count">{usersTotal}</span>
                </h2>
                <button className="ax-btn ax-btn--ghost" onClick={loadUsers}>
                  <IconRefresh /> Обновить
                </button>
              </div>

              <div className="ax-table-wrap">
                <table className="ax-table">
                  <thead>
                    <tr>
                      <th>Пользователь</th>
                      <th>Телефон</th>
                      <th>Проверка</th>
                      <th>KYC</th>
                      <th>Регистрация</th>
                      <th className="ax-right"><span className="ax-sr">Действия</span></th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => {
                      const name = [u.first_name, u.last_name].filter(Boolean).join(' ');
                      return (
                        <tr key={u.id}>
                          <td>
                            <div className="ax-row">
                              <span className="ax-strong">{u.email || <span className="ax-muted">без почты</span>}</span>
                              {u.is_admin && <span className="ax-chip ax-chip--admin">Админ</span>}
                            </div>
                            <div className="ax-sub">
                              ID {u.id}{name ? ` · ${name}` : ''}{u.username ? ` · @${u.username}` : ''}
                            </div>
                          </td>
                          <td className="ax-nowrap">{u.phone_number ? fmtPhone(u.phone_number) : <span className="ax-muted">—</span>}</td>
                          <td>
                            {/* На сайте обязателен только телефон: почта сейчас не проверяется. */}
                            {u.phone_verified ? (
                              <span className="ax-chip ax-chip--ok">Телефон подтверждён</span>
                            ) : (
                              <span className="ax-chip ax-chip--muted">Телефон не подтверждён</span>
                            )}
                            <div className="ax-sub">{u.email_verified ? 'почта подтверждена' : 'почта не подтверждена'}</div>
                          </td>
                          <td>
                            {u.sumsub_status ? (
                              <span className="ax-chip ax-chip--info">{SUMSUB_LABELS[u.sumsub_status] || u.sumsub_status}</span>
                            ) : u.kyc_level ? (
                              <span className="ax-chip ax-chip--ok">Уровень {u.kyc_level}</span>
                            ) : (
                              <span className="ax-muted ax-nowrap">не проходил</span>
                            )}
                          </td>
                          <td className="ax-nowrap">{fmtDate(u.created_at)}</td>
                          <td className="ax-right">
                            <RowMenu
                              items={[
                                {
                                  label: u.is_admin ? 'Снять права админа' : 'Сделать админом',
                                  danger: u.is_admin,
                                  disabled: user?.id === u.id,
                                  onClick: () => toggleUserAdmin(u),
                                },
                                {
                                  label: 'Удалить пользователя',
                                  danger: true,
                                  disabled: user?.id === u.id,
                                  onClick: () => deleteUser(u),
                                },
                              ]}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {users.length === 0 && <div className="ax-empty">Пользователей нет</div>}
            </div>
          </div>
        )}

        {/* Prices */}
        {activeTab === 'prices' && !isLoading && (
          <div>
            <div className="admin-section-header">
              <h2>Цены на товары</h2>
              <button className="admin-add-btn" onClick={() => setEditingPrice({} as PriceConfig)}>
                + Добавить цену
              </button>
            </div>

            <div className="admin-table-container">
              <div className="admin-table-wrapper">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Тип товара</th>
                      <th>Сеть</th>
                      <th>Базовая цена</th>
                      <th>Мин-Макс</th>
                      <th>Скидка</th>
                      <th>Статус</th>
                      <th>Действия</th>
                    </tr>
                  </thead>
                  <tbody>
                    {prices.map((price) => (
                      <tr key={price.id}>
                        <td>
                          <strong>{price.product_type}</strong>
                          {price.label && <><br /><small>{price.label}</small></>}
                        </td>
                        <td>{price.network || '—'}</td>
                        <td>{price.base_price} ₽</td>
                        <td>{price.min_amount} - {price.max_amount}</td>
                        <td>
                          {price.discount_threshold
                            ? `от ${price.discount_threshold}: ${price.discount_percent}%`
                            : '-'}
                        </td>
                        <td>
                          <span className={`admin-status-badge admin-status-badge--${price.is_active ? 'active' : 'inactive'}`}>
                            {price.is_active ? 'Вкл' : 'Выкл'}
                          </span>
                        </td>
                        <td>
                          <div className="admin-actions">
                            <button className="admin-action-btn edit" onClick={() => setEditingPrice(price)}>✎</button>
                            <button className="admin-action-btn delete" onClick={() => deletePrice(price.id)}>🗑</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Payments */}
        {activeTab === 'payments' && !isLoading && (
          <div>
            <div className="admin-section-header">
              <h2>Способы оплаты</h2>
              <button className="admin-add-btn" onClick={() => setEditingPayment({} as PaymentMethod)}>
                + Добавить способ
              </button>
            </div>

            <div className="admin-table-container">
              <div className="admin-table-wrapper">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Код</th>
                      <th>Название</th>
                      <th>Иконка</th>
                      <th>Провайдер</th>
                      <th>Комиссия</th>
                      <th>Статус</th>
                      <th>Действия</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((payment) => (
                      <tr key={payment.id}>
                        <td><code>{payment.code}</code></td>
                        <td>{payment.name}</td>
                        <td>
                          {payment.icon_url && (
                            <img src={payment.icon_url} alt="" style={{ width: 24, height: 24 }} />
                          )}
                        </td>
                        <td>{payment.provider_code || '-'}</td>
                        <td>{payment.commission_text || (payment.commission_percent ? `${payment.commission_percent}%` : '0%')}</td>
                        <td>
                          <span className={`admin-status-badge admin-status-badge--${payment.is_active ? 'active' : 'inactive'}`}>
                            {payment.is_active ? 'Вкл' : 'Выкл'}
                          </span>
                        </td>
                        <td>
                          <div className="admin-actions">
                            <button className="admin-action-btn edit" onClick={() => setEditingPayment(payment)}>✎</button>
                            <button className="admin-action-btn delete" onClick={() => deletePayment(payment.id)}>🗑</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Promos */}
        {activeTab === 'promos' && !isLoading && (
          <div>
            <div className="admin-section-header">
              <h2>Промокоды</h2>
              <button className="admin-add-btn" onClick={() => setEditingPromo({} as PromoCode)}>
                + Добавить промокод
              </button>
            </div>

            <div className="admin-table-container">
              <div className="admin-table-wrapper">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Код</th>
                      <th>Скидка</th>
                      <th>Использовано</th>
                      <th>Мин. сумма</th>
                      <th>Действует до</th>
                      <th>Статус</th>
                      <th>Действия</th>
                    </tr>
                  </thead>
                  <tbody>
                    {promos.map((promo) => (
                      <tr key={promo.id}>
                        <td><code>{promo.code}</code></td>
                        <td>
                          {promo.discount_type === 'percent' ? `${promo.discount_value}%` : `${promo.discount_value} ₽`}
                        </td>
                        <td>{promo.used_count} / {promo.max_uses || '∞'}</td>
                        <td>{promo.min_order_amount ? `${promo.min_order_amount} ₽` : '-'}</td>
                        <td>
                          {promo.valid_until ? new Date(promo.valid_until).toLocaleDateString('ru') : '∞'}
                        </td>
                        <td>
                          <span className={`admin-status-badge admin-status-badge--${promo.is_active ? 'active' : 'inactive'}`}>
                            {promo.is_active ? 'Вкл' : 'Выкл'}
                          </span>
                        </td>
                        <td>
                          <div className="admin-actions">
                            <button className="admin-action-btn edit" onClick={() => setEditingPromo(promo)}>✎</button>
                            <button className="admin-action-btn delete" onClick={() => deletePromo(promo.id)}>🗑</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {promos.length === 0 && (
                <div className="admin-empty">
                  <div className="admin-empty__icon">🎟️</div>
                  <div className="admin-empty__text">Промокодов нет</div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Settings */}
        {activeTab === 'settings' && !isLoading && (
          <div>
            <div className="admin-section-header">
              <h2>Настройки</h2>
              <button className="admin-add-btn" onClick={() => setEditingSetting({} as Setting)}>
                + Добавить настройку
              </button>
            </div>

            <div className="admin-settings-categories">
              <button
                className={`admin-category-btn ${!settingsCategory ? 'admin-category-btn--active' : ''}`}
                onClick={() => setSettingsCategory('')}
              >
                Все
              </button>
              {Object.entries(categoryLabels).map(([key, label]) => (
                <button
                  key={key}
                  className={`admin-category-btn ${settingsCategory === key ? 'admin-category-btn--active' : ''}`}
                  onClick={() => setSettingsCategory(key)}
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="admin-table-container">
              <div className="admin-table-wrapper">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Ключ</th>
                      <th>Значение</th>
                      <th>Категория</th>
                      <th>Публичный</th>
                      <th>Действия</th>
                    </tr>
                  </thead>
                  <tbody>
                    {settings.map((setting) => (
                      <tr key={setting.key}>
                        <td>
                          <code>{setting.key}</code>
                          {setting.label && <><br /><small>{setting.label}</small></>}
                        </td>
                        <td style={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {setting.value}
                        </td>
                        <td>{categoryLabels[setting.category] || setting.category}</td>
                        <td>
                          <span className={`admin-status-badge admin-status-badge--${setting.is_public ? 'active' : 'inactive'}`}>
                            {setting.is_public ? 'Да' : 'Нет'}
                          </span>
                        </td>
                        <td>
                          <div className="admin-actions">
                            <button className="admin-action-btn edit" onClick={() => setEditingSetting(setting)}>✎</button>
                            <button className="admin-action-btn delete" onClick={() => deleteSetting(setting.key)}>🗑</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* FAQ */}
        {activeTab === 'faq' && !isLoading && (
          <div>
            <div className="admin-section-header">
              <h2>FAQ — Частые вопросы</h2>
              <button className="admin-add-btn" onClick={() => setEditingFaq({ id: 0, question: '', answer: '', sort_order: 0, is_active: true })}>
                + Добавить вопрос
              </button>
            </div>

            {faqItems.length > 0 ? (
              <div className="admin-table-container">
                <div className="admin-table-wrapper">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Порядок</th>
                        <th>Вопрос</th>
                        <th>Ответ</th>
                        <th>Активен</th>
                        <th>Действия</th>
                      </tr>
                    </thead>
                    <tbody>
                      {faqItems.map((faq) => (
                        <tr key={faq.id}>
                          <td>{faq.sort_order}</td>
                          <td>{faq.question}</td>
                          <td style={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{faq.answer}</td>
                          <td>{faq.is_active ? '✓' : '✕'}</td>
                          <td>
                            <button className="admin-action-btn" onClick={() => setEditingFaq(faq)}>✎</button>
                            <button className="admin-action-btn delete" onClick={() => deleteFaq(faq.id)}>🗑</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="admin-empty">
                <div className="admin-empty__text">Вопросов пока нет. Добавьте первый FAQ.</div>
              </div>
            )}
          </div>
        )}

        {/* Mock Orders */}
        {activeTab === 'mock' && !isLoading && (
          <div>
            <div className="admin-section-header">
              <h2>Тестовые чеки (Mock Orders)</h2>
              <button className="admin-add-btn" onClick={() => setShowMockModal(true)}>
                + Создать тестовый чек
              </button>
            </div>

            <div className="admin-info-box">
              <p>Здесь вы можете создавать тестовые/фейковые чеки для проверки платежной системы и интеграций.</p>
              <p>Mock заказы создаются с провайдером "mock" и не проходят через реальные платежные системы.</p>
            </div>

            {mockOrders.length > 0 && (
              <div className="receipt-grid">
                {mockOrders.map((order) => (
                  <div
                    key={order.uuid}
                    className="receipt-card"
                    onClick={() => setViewingReceipt(order)}
                  >
                    <div className="receipt-card__header">
                      <div className="receipt-card__logo">Exchange</div>
                      <div className="receipt-card__badge">
                        <span className={`admin-status-badge admin-status-badge--${order.status}`}>
                          {statusLabels[order.status] || order.status}
                        </span>
                      </div>
                    </div>
                    <div className="receipt-card__type">
                      <span className="receipt-card__icon">💵</span>
                      <span>{order.usdt_amount} USDT</span>
                    </div>
                    <div className="receipt-card__amount">{order.amount_rub.toLocaleString()} ₽</div>
                    <div className="receipt-card__recipient">
                      <code>{order.wallet_address?.slice(0, 16)}... ({order.wallet_network})</code>
                    </div>
                    <div className="receipt-card__date">
                      {order.created_at ? new Date(order.created_at).toLocaleString('ru') : '-'}
                    </div>
                    <div className="receipt-card__uuid">#{order.uuid.slice(0, 8)}</div>
                  </div>
                ))}
              </div>
            )}

            {mockOrders.length === 0 && (
              <div className="admin-empty">
                <div className="admin-empty__icon">🧪</div>
                <div className="admin-empty__text">Тестовых чеков пока нет. Нажмите кнопку выше чтобы создать.</div>
              </div>
            )}
          </div>
        )}
          </div>
        </div>
      </main>

      {/* Modals */}
      {editingOrder && (
        <OrderModal
          order={editingOrder}
          onClose={() => setEditingOrder(null)}
          onStatusChange={(status) => {
            updateOrderStatus(editingOrder.uuid, status);
            setEditingOrder(null);
          }}
        />
      )}

      {editingPrice && (
        <PriceModal
          price={editingPrice}
          onSave={savePrice}
          onClose={() => setEditingPrice(null)}
        />
      )}

      {editingPayment && (
        <PaymentModal
          payment={editingPayment}
          onSave={savePayment}
          onClose={() => setEditingPayment(null)}
        />
      )}

      {editingPromo && (
        <PromoModal
          promo={editingPromo}
          onSave={savePromo}
          onClose={() => setEditingPromo(null)}
        />
      )}

      {editingSetting && (
        <SettingModal
          setting={editingSetting}
          onSave={saveSetting}
          onClose={() => setEditingSetting(null)}
        />
      )}

      {editingFaq && (
        <FAQModal
          faq={editingFaq}
          onSave={saveFaq}
          onClose={() => setEditingFaq(null)}
        />
      )}

      {showMockModal && (
        <MockOrderModal
          onSave={createMockOrder}
          onClose={() => setShowMockModal(false)}
        />
      )}

      {viewingReceipt && (
        <ReceiptModal
          order={viewingReceipt}
          onClose={() => setViewingReceipt(null)}
        />
      )}
    </div>
  );
}

// Order Modal
function OrderModal({
  order,
  onClose,
  onStatusChange,
}: {
  order: Order;
  onClose: () => void;
  onStatusChange: (status: string) => void;
}) {
  const statusLabels = ORDER_STATUS_LABELS;

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
        <div className="admin-modal__header">
          <h3>Заказ {order.uuid.slice(0, 8)}</h3>
          <button className="admin-modal__close" onClick={onClose}>×</button>
        </div>
        <div className="admin-modal__body">
          <div className="admin-form-group">
            <label>UUID</label>
            <input type="text" value={order.uuid} readOnly />
          </div>
          <div className="admin-form-group">
            <label>Статус</label>
            <input type="text" value={statusLabels[order.status] || order.status} readOnly />
          </div>
          <div className="admin-form-row">
            <div className="admin-form-group">
              <label>Количество USDT</label>
              <input type="text" value={order.usdt_amount || ''} readOnly />
            </div>
            <div className="admin-form-group">
              <label>Сеть</label>
              <input type="text" value={order.wallet_network || ''} readOnly />
            </div>
          </div>
          <div className="admin-form-group">
            <label>Адрес кошелька</label>
            <input type="text" value={order.wallet_address || ''} readOnly />
          </div>
          <div className="admin-form-row">
            <div className="admin-form-group">
              <label>Сумма</label>
              <input type="text" value={`${order.amount_rub} ₽`} readOnly />
            </div>
            <div className="admin-form-group">
              <label>Провайдер</label>
              <input type="text" value={order.payment_provider || '-'} readOnly />
            </div>
          </div>
          <div className="admin-form-row">
            <div className="admin-form-group">
              <label>Создан</label>
              <input type="text" value={new Date(order.created_at).toLocaleString('ru')} readOnly />
            </div>
            <div className="admin-form-group">
              <label>Оплачен</label>
              <input type="text" value={order.paid_at ? new Date(order.paid_at).toLocaleString('ru') : '-'} readOnly />
            </div>
          </div>
          {order.admin_notes && (
            <div className="admin-form-group">
              <label>Заметки</label>
              <textarea value={order.admin_notes} readOnly />
            </div>
          )}
        </div>
        <div className="admin-modal__footer">
          {['paid', 'processing'].includes(order.status) && (
            <>
              <button className="admin-btn admin-btn--primary" onClick={() => onStatusChange('completed')}>
                Выполнить
              </button>
              <button className="admin-btn admin-btn--danger" onClick={() => onStatusChange('cancelled')}>
                Отменить
              </button>
            </>
          )}
          <button className="admin-btn admin-btn--secondary" onClick={onClose}>Закрыть</button>
        </div>
      </div>
    </div>
  );
}

// Price Modal
function PriceModal({
  price,
  onSave,
  onClose,
}: {
  price: Partial<PriceConfig>;
  onSave: (p: Partial<PriceConfig>) => void;
  onClose: () => void;
}) {
  const [form, setForm] = useState(price);

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
        <div className="admin-modal__header">
          <h3>{price.id ? 'Редактировать цену' : 'Новая цена'}</h3>
          <button className="admin-modal__close" onClick={onClose}>×</button>
        </div>
        <div className="admin-modal__body">
          <div className="admin-form-group">
            <label>Тип продукта</label>
            <input
              type="text"
              value={form.product_type || ''}
              onChange={(e) => setForm({ ...form, product_type: e.target.value })}
              placeholder="usdt, usdt_trc20..."
            />
          </div>
          <div className="admin-form-group">
            <label>Название</label>
            <input
              type="text"
              value={form.label || ''}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              placeholder="USDT"
            />
          </div>
          <div className="admin-form-row">
            <div className="admin-form-group">
              <label>Сеть</label>
              <input
                type="text"
                value={form.network || ''}
                onChange={(e) => setForm({ ...form, network: e.target.value })}
                placeholder="TRC-20, TON, BTC, ERC-20"
              />
            </div>
            <div className="admin-form-group">
              <label>Иконка (URL)</label>
              <input
                type="text"
                value={form.icon_url || ''}
                onChange={(e) => setForm({ ...form, icon_url: e.target.value })}
                placeholder="/usdt.svg"
              />
            </div>
          </div>
          <div className="admin-form-row">
            <div className="admin-form-group">
              <label>Пара Binance для курса</label>
              <input
                type="text"
                value={form.rate_symbol || ''}
                onChange={(e) => setForm({ ...form, rate_symbol: e.target.value })}
                placeholder="TONUSDT (пусто у USDT)"
              />
            </div>
            <div className="admin-form-group">
              <label>Комиссия сети (в монете)</label>
              <input
                type="number"
                step="0.00000001"
                value={form.transfer_fee ?? ''}
                onChange={(e) => setForm({ ...form, transfer_fee: parseFloat(e.target.value) || 0 })}
                placeholder="2"
              />
            </div>
          </div>
          <div className="admin-form-row">
            <div className="admin-form-group">
              <label>Знаков после запятой</label>
              <input
                type="number"
                value={form.decimals ?? 2}
                onChange={(e) => setForm({ ...form, decimals: parseInt(e.target.value) || 0 })}
              />
            </div>
            <div className="admin-form-group">
              <label>Базовая цена (₽)</label>
              <input
                type="number"
                step="0.01"
                value={form.base_price || ''}
                onChange={(e) => setForm({ ...form, base_price: parseFloat(e.target.value) })}
              />
            </div>
            <div className="admin-form-group">
              <label>Порядок сортировки</label>
              <input
                type="number"
                value={form.sort_order || 0}
                onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) })}
              />
            </div>
          </div>
          <div className="admin-form-row">
            <div className="admin-form-group">
              <label>Мин. количество</label>
              <input
                type="number"
                value={form.min_amount || ''}
                onChange={(e) => setForm({ ...form, min_amount: parseInt(e.target.value) })}
              />
            </div>
            <div className="admin-form-group">
              <label>Макс. количество</label>
              <input
                type="number"
                value={form.max_amount || ''}
                onChange={(e) => setForm({ ...form, max_amount: parseInt(e.target.value) })}
              />
            </div>
          </div>
          <div className="admin-form-row">
            <div className="admin-form-group">
              <label>Порог скидки</label>
              <input
                type="number"
                value={form.discount_threshold || ''}
                onChange={(e) => setForm({ ...form, discount_threshold: parseInt(e.target.value) || null })}
                placeholder="1000"
              />
            </div>
            <div className="admin-form-group">
              <label>Скидка (%)</label>
              <input
                type="number"
                value={form.discount_percent || ''}
                onChange={(e) => setForm({ ...form, discount_percent: parseFloat(e.target.value) })}
              />
            </div>
          </div>
          <div className="admin-form-group">
            <label>Описание</label>
            <input
              type="text"
              value={form.description || ''}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="-20%"
            />
          </div>
          <label className="admin-form-checkbox">
            <input
              type="checkbox"
              checked={form.is_active !== false}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
            />
            <span>Активна</span>
          </label>
        </div>
        <div className="admin-modal__footer">
          <button className="admin-btn admin-btn--secondary" onClick={onClose}>Отмена</button>
          <button className="admin-btn admin-btn--primary" onClick={() => onSave(form)}>Сохранить</button>
        </div>
      </div>
    </div>
  );
}

// Payment Modal
function PaymentModal({
  payment,
  onSave,
  onClose,
}: {
  payment: Partial<PaymentMethod>;
  onSave: (p: Partial<PaymentMethod>) => void;
  onClose: () => void;
}) {
  const [form, setForm] = useState(payment);

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
        <div className="admin-modal__header">
          <h3>{payment.id ? 'Редактировать способ оплаты' : 'Новый способ оплаты'}</h3>
          <button className="admin-modal__close" onClick={onClose}>×</button>
        </div>
        <div className="admin-modal__body">
          <div className="admin-form-row">
            <div className="admin-form-group">
              <label>Код</label>
              <input
                type="text"
                value={form.code || ''}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                placeholder="sbp, card, crypto..."
              />
            </div>
            <div className="admin-form-group">
              <label>Название</label>
              <input
                type="text"
                value={form.name || ''}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="СБП"
              />
            </div>
          </div>
          <div className="admin-form-group">
            <label>Описание</label>
            <input
              type="text"
              value={form.description || ''}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </div>
          <div className="admin-form-row">
            <div className="admin-form-group">
              <label>URL иконки</label>
              <input
                type="text"
                value={form.icon_url || ''}
                onChange={(e) => setForm({ ...form, icon_url: e.target.value })}
              />
            </div>
            <div className="admin-form-group">
              <label>Код провайдера</label>
              <input
                type="text"
                value={form.provider_code || ''}
                onChange={(e) => setForm({ ...form, provider_code: e.target.value })}
                placeholder="pay1time, mulenpay..."
              />
            </div>
          </div>
          <div className="admin-form-row">
            <div className="admin-form-group">
              <label>Комиссия (%)</label>
              <input
                type="number"
                step="0.1"
                value={form.commission_percent || ''}
                onChange={(e) => setForm({ ...form, commission_percent: parseFloat(e.target.value) })}
              />
            </div>
            <div className="admin-form-group">
              <label>Фикс. комиссия (₽)</label>
              <input
                type="number"
                value={form.commission_fixed || ''}
                onChange={(e) => setForm({ ...form, commission_fixed: parseFloat(e.target.value) })}
              />
            </div>
          </div>
          <div className="admin-form-group">
            <label>Текст комиссии</label>
            <input
              type="text"
              value={form.commission_text || ''}
              onChange={(e) => setForm({ ...form, commission_text: e.target.value })}
              placeholder="2% + 30₽"
            />
          </div>
          <div className="admin-form-row">
            <div className="admin-form-group">
              <label>Мин. сумма</label>
              <input
                type="number"
                value={form.min_amount || ''}
                onChange={(e) => setForm({ ...form, min_amount: parseFloat(e.target.value) })}
              />
            </div>
            <div className="admin-form-group">
              <label>Макс. сумма</label>
              <input
                type="number"
                value={form.max_amount || ''}
                onChange={(e) => setForm({ ...form, max_amount: parseFloat(e.target.value) || null })}
              />
            </div>
          </div>
          <div className="admin-form-group">
            <label>Порядок сортировки</label>
            <input
              type="number"
              value={form.sort_order || 0}
              onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) })}
            />
          </div>
          <label className="admin-form-checkbox">
            <input
              type="checkbox"
              checked={form.is_active !== false}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
            />
            <span>Активен</span>
          </label>
        </div>
        <div className="admin-modal__footer">
          <button className="admin-btn admin-btn--secondary" onClick={onClose}>Отмена</button>
          <button className="admin-btn admin-btn--primary" onClick={() => onSave(form)}>Сохранить</button>
        </div>
      </div>
    </div>
  );
}

// Promo Modal
function PromoModal({
  promo,
  onSave,
  onClose,
}: {
  promo: Partial<PromoCode>;
  onSave: (p: Partial<PromoCode>) => void;
  onClose: () => void;
}) {
  const [form, setForm] = useState(promo);

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
        <div className="admin-modal__header">
          <h3>{promo.id ? 'Редактировать промокод' : 'Новый промокод'}</h3>
          <button className="admin-modal__close" onClick={onClose}>×</button>
        </div>
        <div className="admin-modal__body">
          <div className="admin-form-group">
            <label>Код</label>
            <input
              type="text"
              value={form.code || ''}
              onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
              placeholder="SUMMER20"
            />
          </div>
          <div className="admin-form-row">
            <div className="admin-form-group">
              <label>Тип скидки</label>
              <select
                value={form.discount_type || 'percent'}
                onChange={(e) => setForm({ ...form, discount_type: e.target.value })}
              >
                <option value="percent">Процент (%)</option>
                <option value="fixed">Фиксированная (₽)</option>
              </select>
            </div>
            <div className="admin-form-group">
              <label>Значение скидки</label>
              <input
                type="number"
                value={form.discount_value || ''}
                onChange={(e) => setForm({ ...form, discount_value: parseFloat(e.target.value) })}
              />
            </div>
          </div>
          <div className="admin-form-row">
            <div className="admin-form-group">
              <label>Макс. использований</label>
              <input
                type="number"
                value={form.max_uses || ''}
                onChange={(e) => setForm({ ...form, max_uses: parseInt(e.target.value) || null })}
                placeholder="∞"
              />
            </div>
            <div className="admin-form-group">
              <label>Мин. сумма заказа</label>
              <input
                type="number"
                value={form.min_order_amount || ''}
                onChange={(e) => setForm({ ...form, min_order_amount: parseFloat(e.target.value) || null })}
              />
            </div>
          </div>
          <div className="admin-form-row">
            <div className="admin-form-group">
              <label>Макс. скидка</label>
              <input
                type="number"
                value={form.max_discount_amount || ''}
                onChange={(e) => setForm({ ...form, max_discount_amount: parseFloat(e.target.value) || null })}
              />
            </div>
            <div className="admin-form-group">
              <label>Действует до</label>
              <input
                type="datetime-local"
                value={form.valid_until ? form.valid_until.slice(0, 16) : ''}
                onChange={(e) => setForm({ ...form, valid_until: e.target.value ? new Date(e.target.value).toISOString() : null })}
              />
            </div>
          </div>
          <div className="admin-form-group">
            <label>Типы товаров (JSON)</label>
            <input
              type="text"
              value={form.product_types || ''}
              onChange={(e) => setForm({ ...form, product_types: e.target.value })}
              placeholder='["usdt"]'
            />
          </div>
          <label className="admin-form-checkbox">
            <input
              type="checkbox"
              checked={form.is_active !== false}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
            />
            <span>Активен</span>
          </label>
        </div>
        <div className="admin-modal__footer">
          <button className="admin-btn admin-btn--secondary" onClick={onClose}>Отмена</button>
          <button className="admin-btn admin-btn--primary" onClick={() => onSave(form)}>Сохранить</button>
        </div>
      </div>
    </div>
  );
}

// Setting Modal
function SettingModal({
  setting,
  onSave,
  onClose,
}: {
  setting: Partial<Setting>;
  onSave: (s: Partial<Setting>) => void;
  onClose: () => void;
}) {
  const [form, setForm] = useState(setting);

  const categoryLabels: Record<string, string> = {
    general: 'Общие', texts: 'Тексты', links: 'Ссылки', footer: 'Футер', images: 'Изображения', payments: 'Платежи',
  };

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
        <div className="admin-modal__header">
          <h3>{setting.id ? 'Редактировать настройку' : 'Новая настройка'}</h3>
          <button className="admin-modal__close" onClick={onClose}>×</button>
        </div>
        <div className="admin-modal__body">
          <div className="admin-form-group">
            <label>Ключ</label>
            <input
              type="text"
              value={form.key || ''}
              onChange={(e) => setForm({ ...form, key: e.target.value })}
              placeholder="site_name"
              disabled={!!setting.id}
            />
          </div>
          <div className="admin-form-group">
            <label>Название</label>
            <input
              type="text"
              value={form.label || ''}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              placeholder="Название сайта"
            />
          </div>
          <div className="admin-form-group">
            <label>Значение</label>
            <textarea
              value={form.value || ''}
              onChange={(e) => setForm({ ...form, value: e.target.value })}
              rows={3}
            />
          </div>
          <div className="admin-form-row">
            <div className="admin-form-group">
              <label>Категория</label>
              <select
                value={form.category || 'general'}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
              >
                {Object.entries(categoryLabels).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
            <div className="admin-form-group">
              <label>Тип</label>
              <select
                value={form.type || 'string'}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
              >
                <option value="string">Строка</option>
                <option value="text">Текст</option>
                <option value="number">Число</option>
                <option value="boolean">Логический</option>
                <option value="json">JSON</option>
              </select>
            </div>
          </div>
          <div className="admin-form-group">
            <label>Описание</label>
            <input
              type="text"
              value={form.description || ''}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </div>
          <label className="admin-form-checkbox">
            <input
              type="checkbox"
              checked={form.is_public !== false}
              onChange={(e) => setForm({ ...form, is_public: e.target.checked })}
            />
            <span>Публичная настройка (доступна без авторизации)</span>
          </label>
        </div>
        <div className="admin-modal__footer">
          <button className="admin-btn admin-btn--secondary" onClick={onClose}>Отмена</button>
          <button className="admin-btn admin-btn--primary" onClick={() => onSave(form)}>Сохранить</button>
        </div>
      </div>
    </div>
  );
}

// Mock Order Modal
function MockOrderModal({
  onSave,
  onClose,
}: {
  onSave: (form: MockOrderForm) => void;
  onClose: () => void;
}) {
  const [form, setForm] = useState<MockOrderForm>({
    usdt_amount: 10,
    wallet_address: '',
    wallet_network: 'TRC-20',
    amount_rub: 250,
    status: 'completed',
    payment_provider: 'mock',
  });

  const handleSubmit = () => {
    if (!form.wallet_address) {
      alert('Введите адрес кошелька');
      return;
    }
    if (!form.usdt_amount) {
      alert('Введите количество USDT');
      return;
    }
    onSave(form);
  };

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
        <div className="admin-modal__header">
          <h3>Создать тестовый чек</h3>
          <button className="admin-modal__close" onClick={onClose}>×</button>
        </div>
        <div className="admin-modal__body">
          <div className="admin-form-group">
            <label>Количество USDT</label>
            <input
              type="number"
              value={form.usdt_amount || ''}
              onChange={(e) => setForm({ ...form, usdt_amount: parseFloat(e.target.value) || null })}
              placeholder="10"
              min="1"
              step="0.01"
            />
          </div>
          <div className="admin-form-group">
            <label>Адрес кошелька</label>
            <input
              type="text"
              value={form.wallet_address || ''}
              onChange={(e) => setForm({ ...form, wallet_address: e.target.value })}
              placeholder="T..."
            />
          </div>
          <div className="admin-form-group">
            <label>Сеть</label>
            <select
              value={form.wallet_network || 'TRC-20'}
              onChange={(e) => setForm({ ...form, wallet_network: e.target.value })}
            >
              <option value="TRC-20">TRC-20 (TRON)</option>
              <option value="ERC-20">ERC-20 (Ethereum)</option>
            </select>
          </div>

          <div className="admin-form-group">
            <label>Сумма в рублях</label>
            <input
              type="number"
              value={form.amount_rub}
              onChange={(e) => setForm({ ...form, amount_rub: parseFloat(e.target.value) || 0 })}
              placeholder="250"
              min="1"
              step="0.01"
            />
          </div>

          <div className="admin-form-group">
            <label>Статус заказа</label>
            <select
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
            >
              <option value="pending">Ожидает</option>
              <option value="waiting_payment">Ожидает оплату</option>
              <option value="paid">Оплачен</option>
              <option value="processing">В обработке</option>
              <option value="completed">Выполнен</option>
              <option value="failed">Ошибка</option>
              <option value="cancelled">Отменён</option>
              <option value="expired">Истёк</option>
            </select>
          </div>

          <div className="admin-form-group">
            <label>Провайдер оплаты</label>
            <select
              value={form.payment_provider}
              onChange={(e) => setForm({ ...form, payment_provider: e.target.value })}
            >
              <option value="mock">Mock (тестовый)</option>
              <option value="sbp">СБП</option>
              <option value="card">Банковская карта</option>
              <option value="crypto">Криптовалюта</option>
            </select>
          </div>
        </div>
        <div className="admin-modal__footer">
          <button className="admin-btn admin-btn--secondary" onClick={onClose}>Отмена</button>
          <button className="admin-btn admin-btn--primary" onClick={handleSubmit}>Создать чек</button>
        </div>
      </div>
    </div>
  );
}

// Payment Success Page Modal
function ReceiptModal({
  order,
  onClose,
}: {
  order: Order;
  onClose: () => void;
}) {
  const statusLabels: Record<string, string> = {
    pending: 'Ожидает оплаты', waiting_payment: 'Ожидает оплаты', paid: 'Оплачен',
    processing: 'Обрабатывается', completed: 'Выполнен', failed: 'Ошибка',
    cancelled: 'Отменён', expired: 'Истёк',
  };

  const statusIcons: Record<string, string> = {
    pending: '⏳', waiting_payment: '⏳', paid: '✓',
    processing: '⚡', completed: '✓', failed: '✕',
    cancelled: '✕', expired: '⏰',
  };

  const providerLabels: Record<string, string> = {
    mock: 'Тестовый',
    sbp: 'СБП',
    card: 'Банковская карта',
    crypto: 'Криптовалюта',
  };

  const isSuccess = ['paid', 'processing', 'completed'].includes(order.status);

  return (
    <div className="payment-page-overlay" onClick={onClose}>
      <div className="payment-page" onClick={(e) => e.stopPropagation()}>
        <button className="payment-page__close" onClick={onClose}>×</button>

        <div className="payment-page__content">
          {/* Status Icon */}
          <div className={`payment-page__status-icon ${isSuccess ? 'success' : 'error'}`}>
            {statusIcons[order.status] || '?'}
          </div>

          {/* Status Title */}
          <h1 className="payment-page__title">
            {isSuccess ? 'Оплата прошла успешно!' : statusLabels[order.status]}
          </h1>

          {/* Order Type */}
          <div className="payment-page__product">
            <span className="payment-page__product-icon">💵</span>
            <span className="payment-page__product-name">{order.usdt_amount} USDT</span>
          </div>

          {/* Amount */}
          <div className="payment-page__amount">
            {order.amount_rub.toLocaleString()} ₽
          </div>

          {/* Details Card */}
          <div className="payment-page__details">
            <div className="payment-page__detail-row">
              <span className="payment-page__detail-label">Номер заказа</span>
              <span className="payment-page__detail-value">#{order.uuid.slice(0, 8).toUpperCase()}</span>
            </div>
            <div className="payment-page__detail-row">
              <span className="payment-page__detail-label">Кошелёк</span>
              <span className="payment-page__detail-value">
                <code>{order.wallet_address?.slice(0, 16)}...</code> ({order.wallet_network})
              </span>
            </div>
            <div className="payment-page__detail-row">
              <span className="payment-page__detail-label">Способ оплаты</span>
              <span className="payment-page__detail-value">{providerLabels[order.payment_provider || ''] || order.payment_provider}</span>
            </div>
            <div className="payment-page__detail-row">
              <span className="payment-page__detail-label">Дата</span>
              <span className="payment-page__detail-value">
                {order.created_at ? new Date(order.created_at).toLocaleString('ru') : '-'}
              </span>
            </div>
            <div className="payment-page__detail-row">
              <span className="payment-page__detail-label">Статус</span>
              <span className={`payment-page__status-badge ${order.status}`}>
                {statusLabels[order.status]}
              </span>
            </div>
          </div>

          {/* Info Message */}
          {isSuccess && (
            <div className="payment-page__info">
              <p>USDT будет отправлен на указанный кошелёк в течение нескольких минут.</p>
            </div>
          )}

          {/* Support Link */}
          <div className="payment-page__support">
            <span>Возникли вопросы?</span>
            <a href="#" target="_blank" rel="noopener noreferrer">
              Написать в поддержку
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

// FAQ Modal
function FAQModal({
  faq,
  onSave,
  onClose,
}: {
  faq: FAQItem;
  onSave: (faq: Partial<FAQItem>) => void;
  onClose: () => void;
}) {
  const [question, setQuestion] = useState(faq.question);
  const [answer, setAnswer] = useState(faq.answer);
  const [sortOrder, setSortOrder] = useState(faq.sort_order);
  const [isActive, setIsActive] = useState(faq.is_active);

  const handleSave = () => {
    if (!question.trim() || !answer.trim()) return;
    onSave({
      ...(faq.id ? { id: faq.id } : {}),
      question: question.trim(),
      answer: answer.trim(),
      sort_order: sortOrder,
      is_active: isActive,
    });
  };

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
        <div className="admin-modal__header">
          <h3>{faq.id ? 'Редактировать вопрос' : 'Новый вопрос'}</h3>
          <button className="admin-modal__close" onClick={onClose}>×</button>
        </div>
        <div className="admin-modal__body">
          <div className="admin-form-group">
            <label>Вопрос</label>
            <input type="text" value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="Как быстро приходит USDT?" />
          </div>
          <div className="admin-form-group">
            <label>Ответ</label>
            <textarea value={answer} onChange={(e) => setAnswer(e.target.value)} rows={4} placeholder="USDT отправляется в течение 5-15 минут..." />
          </div>
          <div className="admin-form-row">
            <div className="admin-form-group">
              <label>Порядок сортировки</label>
              <input type="number" value={sortOrder} onChange={(e) => setSortOrder(Number(e.target.value))} />
            </div>
            <div className="admin-form-group">
              <label>
                <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} style={{ marginRight: 8 }} />
                Активен
              </label>
            </div>
          </div>
        </div>
        <div className="admin-modal__footer">
          <button className="admin-btn" onClick={onClose}>Отмена</button>
          <button className="admin-btn admin-btn--primary" onClick={handleSave}>Сохранить</button>
        </div>
      </div>
    </div>
  );
}
